import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { getUpcomingFlights, createFlight } from '../services/api';
import { useAuth } from '../context/AuthContext';
import checkmarkIcon from '../assets/icons/checkmark.svg';
import './FlyAircraftModal.css';

const FLIGHT_NUMBER_PATTERN = /^(?:WZZ|WMT|WUK|W6|W4|W9)\d{3,4}$/;
const ICAO_PATTERN = /^[A-Z]{4}$/;

const FlyAircraftModal = ({ onClose, onSuccess }) => {
  const { aircraft } = useAuth();
  const [form, setForm] = useState({
    date: format(new Date(), 'dd.MMM.yyyy HH:mm'),
    flight_number: '',
    from_airport: '',
    to_airport: '',
  });
  const [upcomingFlights, setUpcomingFlights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const normalizeFlightNumberInput = value =>
    value.toUpperCase().replace(/\s+/g, '').replace(/[^A-Z0-9]/g, '');
  const normalizeAirportInput = value =>
    value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 4);

  const isFlightNumberValid = FLIGHT_NUMBER_PATTERN.test(form.flight_number);
  const isFromAirportValid = ICAO_PATTERN.test(form.from_airport);
  const isToAirportValid = ICAO_PATTERN.test(form.to_airport);
  const hasValidRoute = isFromAirportValid && isToAirportValid;
  const canSubmit = isFlightNumberValid && hasValidRoute;

  useEffect(() => {
    if (!aircraft) return;
    setLoading(true);
    getUpcomingFlights(aircraft.id)
      .catch(() => ({ data: { flights: [] } }))
      .then((flightsRes) => {
      setUpcomingFlights(flightsRes.data.flights || []);
      })
      .finally(() => setLoading(false));
  }, [aircraft]);

  const handleChange = e => {
    const { name, value } = e.target;
    if (name === 'flight_number') {
      setForm(f => ({ ...f, [name]: normalizeFlightNumberInput(value) }));
    } else if (name === 'from_airport' || name === 'to_airport') {
      setForm(f => ({ ...f, [name]: normalizeAirportInput(value) }));
    } else {
      setForm(f => ({ ...f, [name]: value }));
    }
    setError('');
  };

  const handleSelectFlight = flight => {
    setForm(f => ({
      ...f,
      flight_number: normalizeFlightNumberInput(flight.flight_number || ''),
      from_airport: normalizeAirportInput(flight.from_airport || ''),
      to_airport: normalizeAirportInput(flight.to_airport || ''),
    }));
  };

  const handleSubmit = async () => {
    if (!isFlightNumberValid) {
      setError('Flight number must start with WZZ/WMT/WUK/W6/W4/W9 and end with 3 or 4 digits');
      return;
    }
    if (!isFromAirportValid) {
      setError('Departure airport ICAO must be 4 letters');
      return;
    }
    if (!isToAirportValid) {
      setError('Arrival airport ICAO must be 4 letters');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const res = await createFlight({
        aircraft_id: aircraft.id,
        flight_number: form.flight_number.trim().toUpperCase(),
        from_airport: form.from_airport.toUpperCase(),
        to_airport: form.to_airport.toUpperCase(),
        date_of_flight: new Date().toISOString()
      });
      onSuccess(res.data.flight);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start flight');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="fly-modal" onClick={e => e.stopPropagation()}>
        
        {/* Header */}
        <div className="fly-modal-header">
          <span>Fly Aircraft</span>
          <button className="fly-close" onClick={onClose}>×</button>
        </div>

        {/* Body */}
        <div className="fly-modal-body">
          {error && <div className="fly-error">{error}</div>}
          
          {/* Inner Card Container */}
          <div className="fly-inner-card">
            
            {/* Date Row */}
            <div className="fly-row">
              <label className="fly-label">Date</label>
              <div className="fly-input-wrapper fly-input-readonly">
                <input value={form.date} readOnly />
              </div>
            </div>

            {/* Select Flight Row */}
            <div className="fly-row">
              <label className="fly-label">Select Flight</label>
              <div className="fly-input-wrapper">
                <select
                  className="ui-dropdown fly-select"
                  onChange={e => {
                    const flight = upcomingFlights[parseInt(e.target.value)];
                    if (flight) handleSelectFlight(flight);
                    e.target.value = '';
                  }}
                  disabled={loading || upcomingFlights.length === 0}
                >
                  <option value="" hidden>
                    {loading ? 'Loading...' : upcomingFlights.length === 0
                      ? 'No upcoming flights'
                      : 'Quick select from flight list'}
                  </option>
                  {upcomingFlights.map((f, i) => (
                    <option key={f.id} value={i}>
                      {f.flight_number} — {f.from_airport} → {f.to_airport}
                    </option>
                  ))}
                </select>
                <div>
                </div>
              </div>
            </div>

            {/* Flight Number Row */}
            <div className="fly-row">
              <label className="fly-label">Flight Number</label>
              <div className={`fly-input-wrapper fly-required-mark ${isFlightNumberValid ? 'complete' : 'incomplete'}`}>
                <input 
                  name="flight_number" 
                  autoFocus
                  value={form.flight_number} 
                  onChange={handleChange} 
                  autoComplete="off"
                  maxLength={7}
                />
              </div>
            </div>
            <div className="fly-row fly-row-hint">
              <div />
            </div>

            {/* From - To Row */}
            <div className="fly-row">
              <label className="fly-label">From — To</label>
              <div className="fly-split-row">
                
                {/* From Airport */}
                <div className={`fly-input-wrapper fly-required-mark fly-split-input ${isFromAirportValid ? 'complete' : 'incomplete'}`}>
                  <input
                    name="from_airport"
                    value={form.from_airport}
                    onChange={handleChange}
                    autoComplete="off"
                    maxLength={4}
                  />
                </div>

                <span className="fly-split-separator">–</span>

                {/* To Airport */}
                <div className={`fly-input-wrapper fly-required-mark fly-split-input ${isToAirportValid ? 'complete' : 'incomplete'}`}>
                  <input
                    name="to_airport"
                    value={form.to_airport}
                    onChange={handleChange}
                    autoComplete="off"
                    maxLength={4}
                  />
                </div>

              </div>
            </div>

          </div>
        </div>

        {/* Footer */}
        <div className="fly-modal-footer">
          <button className="fly-confirm-btn" onClick={handleSubmit} disabled={saving || !canSubmit}>
            {saving ? (
              'Starting...'
            ) : (
              <>
                <span className="fly-check-icon"><img src={checkmarkIcon} alt="" className="fly-icon-img" /></span> Fly Aircraft
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FlyAircraftModal;
